import { Menu } from '@mantine/core';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ApplicationParams } from 'types/route_params';
import { Dashboard } from 'types/dashboard';
import { isDataWidget } from 'types/dashboard_widget';
import { MonitorRoute, NavRoute } from 'application/types/routes';

import { exportDashboardAsJson } from 'dashboards/services/dashboard';
import { useCreateDashboard } from 'dashboards/hooks/useCreateDashboard';
import { useDeleteDashboard } from 'dashboards/hooks/useDeleteDashboard';
import { useExportDashboardData } from 'dashboards/hooks/useExportDashboardData';
import { useTimeRangeParams } from 'dashboards/hooks/useTimeRangeParams';
import { useModal } from 'hooks/useModal';
import { useTagsFilterParams } from 'tags/hooks/useTagsFilterParams';
import { useAccount, useSetDefaultDashboard } from 'hooks/api/useAccount';
import { getDefaultDashboard } from 'services/defaultDashboards';

import { Icon } from 'components/Icon/IconDeprecated';
import { IconButton } from 'components/IconButton/IconButtonDeprecated';
import { SidebarLink } from 'components/Sidebar/SidebarLink';

type DashboardLinkProps = {
  dashboard: Dashboard;
};

export function DashboardLink({ dashboard }: DashboardLinkProps) {
  const navigate = useNavigate();

  const { applicationID } = useParams<ApplicationParams>();
  const { open } = useModal();
  const { tagged_with } = useTagsFilterParams();
  const { data: account } = useAccount();

  const [searchParams] = useSearchParams();

  const { start, stop, timezone } = useTimeRangeParams();
  const { exportDashboard, isExporting } = useExportDashboardData();

  const gatewayId = searchParams.get('gateway_id') ?? undefined;
  const tags = searchParams.get('tags')?.split(',');

  const hasDataWidgets = dashboard.configuration.widgets.some(isDataWidget);

  function handleExportDataClick() {
    exportDashboard({
      dashboard,
      timeRange: { start, stop },
      timezone,
      gatewayId,
      tags,
    });
  }

  const updatedSearchParams = new URLSearchParams(searchParams);

  if (tagged_with?.length) {
    updatedSearchParams.set('tags', tagged_with.join(','));
  }

  const search = updatedSearchParams.toString();

  const [requestDeleteDashboard] = useDeleteDashboard({
    onSuccess() {
      navigate({
        pathname: `/applications/${applicationID}/${NavRoute.MONITOR}/dashboards`,
        search,
      });
    },
  });

  function handleDeleteClick() {
    requestDeleteDashboard(dashboard.id);
  }

  const { mutate: createDashboard } = useCreateDashboard({
    onSuccess(dashboard) {
      navigate({
        pathname: `/applications/${applicationID}/${NavRoute.MONITOR}/${MonitorRoute.DASHBOARDS}/${dashboard.id}`,
        search,
      });
    },
  });

  function handleDuplicateClick() {
    createDashboard({
      name: `Copy of ${dashboard.name}`,
      configuration: dashboard.configuration,
    });
  }

  const { mutate: setDefaultDashboard } = useSetDefaultDashboard();

  function handleSetDefaultDashboard() {
    if (!applicationID) return;
    setDefaultDashboard({
      applicationId: applicationID,
      dashboardId: dashboard.id,
    });
  }

  function handleClearDefaultDashboard() {
    if (!applicationID) return;
    setDefaultDashboard({
      applicationId: applicationID,
      dashboardId: undefined,
    });
  }

  const defaultDashboardId = getDefaultDashboard(
    account?.account_attributes?.default_dashboards,
    applicationID
  );
  const isDefaultDashboard = defaultDashboardId === dashboard.id;

  return (
    <SidebarLink
      to={{
        pathname: `/${NavRoute.MONITOR}/${MonitorRoute.DASHBOARDS}/${dashboard.id}`,
        search,
      }}
      action={
        <Menu>
          <Menu.Target>
            <IconButton
              size="small"
              icon="more-vertical"
              label={`More options for ${dashboard.name}`}
            />
          </Menu.Target>

          <Menu.Dropdown>
            {isDefaultDashboard ? (
              <Menu.Item
                onClick={handleClearDefaultDashboard}
                leftSection={<Icon name="dashboard" size="small" />}
              >
                Clear default dashboard
              </Menu.Item>
            ) : (
              <Menu.Item
                onClick={handleSetDefaultDashboard}
                leftSection={<Icon name="dashboard" size="small" />}
              >
                Set as default dashboard
              </Menu.Item>
            )}

            <Menu.Divider />

            <Menu.Item
              onClick={() => open('rename-dashboard', { dashboard })}
              leftSection={<Icon name="settings" size="small" />}
            >
              Rename
            </Menu.Item>
            <Menu.Item
              onClick={handleDuplicateClick}
              leftSection={<Icon name="copy" size="small" />}
            >
              Duplicate
            </Menu.Item>
            <Menu.Item
              onClick={() => exportDashboardAsJson(dashboard)}
              leftSection={<Icon name="export" size="small" />}
            >
              Export as JSON
            </Menu.Item>
            <Menu.Item
              onClick={handleExportDataClick}
              leftSection={<Icon name="export" size="small" />}
              disabled={!hasDataWidgets || isExporting}
            >
              {isExporting ? 'Exporting...' : 'Export Data as Excel'}
            </Menu.Item>

            <Menu.Divider />

            <Menu.Item
              color="red"
              onClick={handleDeleteClick}
              leftSection={<Icon name="delete" size="small" unstyled />}
            >
              Delete
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      }
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {dashboard.name}
        {isDefaultDashboard && (
          <span
            style={{
              fontSize: '0.75rem',
              opacity: 0.7,
            }}
            title="Default dashboard"
          >
            📌
          </span>
        )}
      </span>
    </SidebarLink>
  );
}
